import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import { api } from "../lib/api";
import { TIPOS_EVENTO } from "../lib/constants";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Switch } from "../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft,
  Calendar,
  MapPin,
  Phone,
  Mail,
  User,
  FileText,
  Trash2,
  Edit,
  Save,
  X,
  Plus,
  CheckCircle2,
  Package,
  Clock,
  Receipt,
  Euro,
  CreditCard,
  UserMinus,
  Download,
} from "lucide-react";

import { generatePDF } from "../lib/pdfGenerator";

// Packs predefinidos
const PACKS = [
  { nombre: "Pack Garbí", precio: 360 },
  { nombre: "Pack Gregal", precio: 430 },
  { nombre: "Pack Tramuntana", precio: 520 },
  { nombre: "Pack Personalizado", precio: 0 },
];

// Servicios extra predefinidos
const SERVICIOS_EXTRA = [
  { nombre: "Hora extra", precio: 80, unidad: "hora" },
  { nombre: "Álbum Básico", precio: 35, unidad: "unidad" },
  { nombre: "Álbum Deluxe", precio: 70, unidad: "unidad" },
  { nombre: "Atrezzo", precio: 30, unidad: "unidad" },
  { nombre: "Desplazamiento", precio: 0.60, unidad: "km" },
  { nombre: "Borrar marca de agua", precio: 60, unidad: "unidad" },
];



const formatDate = (value) => {
  if (!value || value === "no hay datos" || value === "No hay datos") return value || "Sin fecha";
  try {
    const date = parseISO(value);
    if (isNaN(date.getTime())) return value || "Sin fecha";
    return format(date, "dd-MM-yyyy", { locale: es });
  } catch (error) {
    return value || "Sin fecha";
  }
};

export default function ClienteDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [cliente, setCliente] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [newTaskTitle, setNewTaskTitle] = useState("");
  const [saving, setSaving] = useState(false);
  const [exportingPDF, setExportingPDF] = useState(false);

  // Estado para añadir servicios/pagos
  const [addServiceDialog, setAddServiceDialog] = useState(false);
  const [addPaymentDialog, setAddPaymentDialog] = useState(false);
  const [newService, setNewService] = useState({ concepto: "", cantidad: 1, precio_unitario: 0 });
  const [newPayment, setNewPayment] = useState({ fecha: "", cantidad: 0, concepto: "pago_parcial" });
  const [customServiceName, setCustomServiceName] = useState("");

  const loadCliente = useCallback(async () => {
    try {
      const response = await api.getCliente(id);
      setCliente(response.data);
      setFormData({
        nombre: response.data.nombre || "",
        telefono: response.data.telefono || "",
        email: response.data.email || "",
        dni_cif_nif: response.data.dni_cif_nif || "",
        fecha_evento: response.data.fecha_evento || "",
        tipo_evento: response.data.tipo_evento || "",
        ubicacion: response.data.ubicacion || "",
        notas: response.data.notas || "",
        hora_inicio: response.data.hora_inicio || "",
        hora_fin: response.data.hora_fin || "",
      });
    } catch (error) {
      toast.error("Error al cargar el cliente");
      navigate("/clientes");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const loadTasks = useCallback(async () => {
    try {
      const response = await api.getTasks("cliente", id);
      setTasks(response.data);
    } catch (error) {
      console.error("Error loading tasks:", error);
    }
  }, [id]);

  useEffect(() => {
    loadCliente();
    loadTasks();
  }, [id, loadCliente, loadTasks]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await api.updateCliente(id, formData);
      setCliente(response.data);
      setEditing(false);
      toast.success("Cliente actualizado");
    } catch (error) {
      toast.error("Error al actualizar");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.deleteCliente(id);
      toast.success("Cliente eliminado");
      navigate("/clientes");
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const handleDesconvertir = async () => {
    try {
      await api.desconvertirCliente(id);
      toast.success("Cliente desconvertido. Consulta marcada como descartada.");
      navigate("/consultas");
    } catch (error) {
      toast.error("Error al desconvertir");
    }
  };

  // Funciones de facturación
  const addLineaFactura = async (linea) => {
    const nuevasLineas = [...(cliente.lineas_factura || []), linea];
    try {
      const response = await api.updateCliente(id, { lineas_factura: nuevasLineas });
      setCliente(response.data);
      toast.success("Línea añadida");
      setAddServiceDialog(false);
      setNewService({ concepto: "", cantidad: 1, precio_unitario: 0 });
      setCustomServiceName("");
    } catch (error) {
      toast.error("Error al añadir línea");
    }
  };

  const removeLineaFactura = async (index) => {
    const nuevasLineas = cliente.lineas_factura.filter((_, i) => i !== index);
    try {
      const response = await api.updateCliente(id, { lineas_factura: nuevasLineas });
      setCliente(response.data);
      toast.success("Línea eliminada");
    } catch (error) {
      toast.error("Error al eliminar línea");
    }
  };

  const toggleIVA = async () => {
    try {
      const response = await api.updateCliente(id, { aplicar_iva: !cliente.aplicar_iva });
      setCliente(response.data);
    } catch (error) {
      toast.error("Error al actualizar IVA");
    }
  };

  // Funciones de pagos
  const addPago = async () => {
    if (!newPayment.fecha || newPayment.cantidad <= 0) {
      toast.error("Completa la fecha y cantidad");
      return;
    }
    const nuevosPagos = [...(cliente.pagos || []), newPayment];
    try {
      const response = await api.updateCliente(id, { pagos: nuevosPagos });
      setCliente(response.data);
      toast.success("Pago registrado");
      setAddPaymentDialog(false);
      setNewPayment({ fecha: "", cantidad: 0, concepto: "pago_parcial" });
    } catch (error) {
      toast.error("Error al registrar pago");
    }
  };

  const removePago = async (index) => {
    const nuevosPagos = cliente.pagos.filter((_, i) => i !== index);
    try {
      const response = await api.updateCliente(id, { pagos: nuevosPagos });
      setCliente(response.data);
      toast.success("Pago eliminado");
    } catch (error) {
      toast.error("Error al eliminar pago");
    }
  };

  // Funciones de tareas
  const handleAddTask = async () => {
    if (!newTaskTitle.trim()) return;
    try {
      await api.createTask({
        titulo: newTaskTitle,
        entidad_tipo: "cliente",
        entidad_id: id,
      });
      setNewTaskTitle("");
      loadTasks();
      toast.success("Tarea añadida");
    } catch (error) {
      toast.error("Error al añadir tarea");
    }
  };

  const handleToggleTask = async (task) => {
    try {
      await api.updateTask(task.id, { completada: !task.completada });
      loadTasks();
    } catch (error) {
      toast.error("Error al actualizar tarea");
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await api.deleteTask(taskId);
      loadTasks();
    } catch (error) {
      toast.error("Error al eliminar tarea");
    }
  };

  // Helpers
  const handleSelectPack = (pack) => {
    const linea = {
      concepto: pack.nombre,
      cantidad: 1,
      precio_unitario: pack.precio,
      subtotal: pack.precio
    };
    if (pack.precio === 0) {
      // Pack personalizado - mostrar input
      setNewService({ concepto: pack.nombre, cantidad: 1, precio_unitario: 0 });
    } else {
      addLineaFactura(linea);
    }
  };

  const handleSelectServicioExtra = (servicio) => {
    setNewService({
      concepto: servicio.nombre,
      cantidad: 1,
      precio_unitario: servicio.precio,
    });
  };

  const handleAddCustomService = () => {
    if (!newService.concepto || newService.precio_unitario <= 0) {
      toast.error("Completa el concepto y precio");
      return;
    }
    const linea = {
      concepto: newService.concepto === "Otro" ? customServiceName : newService.concepto,
      cantidad: newService.cantidad,
      precio_unitario: newService.precio_unitario,
      subtotal: newService.cantidad * newService.precio_unitario
    };
    addLineaFactura(linea);
  };

  const handleExportPDF = async () => {
    setExportingPDF(true);
    try {
      const configRes = await api.getEmpresaConfig();
      generatePDF(cliente, configRes.data);
      toast.success("Factura generada y descargada");
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Error al generar el PDF");
    } finally {
      setExportingPDF(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-pulse text-primary font-heading text-xl">Cargando...</div>
      </div>
    );
  }

  const pendingTasks = tasks.filter((t) => !t.completada);
  const completedTasks = tasks.filter((t) => t.completada);
  const progress = tasks.length > 0 ? Math.round((completedTasks.length / tasks.length) * 100) : 0;

  const estadoPagoColor = {
    pendiente: "bg-red-100 text-red-700 border-red-200",
    reservado: "bg-amber-100 text-amber-700 border-amber-200",
    pagado: "bg-green-100 text-green-700 border-green-200",
  };

  const estadoPagoLabel = {
    pendiente: "Pendiente",
    reservado: "Reservado",
    pagado: "Pagado",
  };

  return (
    <div data-testid="cliente-detail-page" className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/clientes")} className="rounded-full">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-heading text-2xl lg:text-3xl font-bold text-primary">
                {cliente.nombre}
              </h1>
              <Badge className="bg-green-100 text-green-700 border-green-200" variant="outline">
                Cliente
              </Badge>
              <Badge className={estadoPagoColor[cliente.estado_pago]} variant="outline">
                {estadoPagoLabel[cliente.estado_pago]}
              </Badge>
            </div>
            <p className="text-muted-foreground mt-1">{cliente.tipo_evento}</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" className="rounded-full gap-2 border-amber-500/50 text-amber-600 hover:bg-amber-500 hover:text-white">
                <UserMinus className="w-4 h-4" />
                Desconvertir
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>¿Desconvertir cliente?</AlertDialogTitle>
                <AlertDialogDescription>
                  Se eliminará el cliente y la consulta original se marcará como descartada.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="rounded-full">Cancelar</AlertDialogCancel>
                <AlertDialogAction onClick={handleDesconvertir} className="rounded-full bg-amber-500 hover:bg-amber-600">
                  Desconvertir
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {!editing ? (
            <Button variant="outline" onClick={() => setEditing(true)} className="rounded-full gap-2">
              <Edit className="w-4 h-4" />
              Editar
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => { setEditing(false); loadCliente(); }} className="rounded-full gap-2">
                <X className="w-4 h-4" />
                Cancelar
              </Button>
              <Button onClick={handleSave} className="rounded-full bg-primary gap-2" disabled={saving}>
                <Save className="w-4 h-4" />
                {saving ? "Guardando..." : "Guardar"}
              </Button>
            </>
          )}
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="ghost" size="icon" className="text-destructive hover:bg-destructive/10">
                <Trash2 className="w-4 h-4" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>¿Eliminar cliente?</AlertDialogTitle>
                <AlertDialogDescription>
                  Esta acción no se puede deshacer.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="rounded-full">Cancelar</AlertDialogCancel>
                <AlertDialogAction onClick={handleDelete} className="rounded-full bg-destructive">
                  Eliminar
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {/* Progress Bar */}
      <Card className="border-0 shadow-soft bg-gradient-to-r from-primary/5 to-secondary/5">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-primary">Progreso del evento</span>
            <span className="text-sm font-bold text-primary">{progress}%</span>
          </div>
          <div className="w-full h-3 bg-muted rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-primary to-secondary transition-all duration-500" style={{ width: `${progress}%` }} />
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            {completedTasks.length} de {tasks.length} tareas completadas
          </p>
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Info Card */}
        <Card className="lg:col-span-2 border-0 shadow-soft">
          <CardHeader>
            <CardTitle className="font-heading text-xl">Información del Cliente</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {editing ? (
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nombre</Label>
                  <Input value={formData.nombre} onChange={(e) => setFormData({ ...formData, nombre: e.target.value })} className="h-11 rounded-xl" />
                </div>
                <div className="space-y-2">
                  <Label>Teléfono</Label>
                  <Input value={formData.telefono} onChange={(e) => setFormData({ ...formData, telefono: e.target.value })} className="h-11 rounded-xl" />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="h-11 rounded-xl" />
                </div>
                <div className="space-y-2">
                  <Label>DNI/CIF/NIF (opcional)</Label>
                  <Input value={formData.dni_cif_nif || ""} onChange={(e) => setFormData({ ...formData, dni_cif_nif: e.target.value })} className="h-11 rounded-xl" />
                </div>
                <div className="space-y-2">
                  <Label>Tipo de Evento</Label>
                  <Select value={formData.tipo_evento} onValueChange={(v) => setFormData({ ...formData, tipo_evento: v })}>
                    <SelectTrigger className="h-11 rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TIPOS_EVENTO.map((tipo) => (
                        <SelectItem key={tipo} value={tipo}>{tipo}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Fecha Evento</Label>
                  <Input type="date" value={formData.fecha_evento} onChange={(e) => setFormData({ ...formData, fecha_evento: e.target.value })} className="h-11 rounded-xl" />
                </div>
                <div className="space-y-2">
                  <Label>Ubicación</Label>
                  <Input value={formData.ubicacion} onChange={(e) => setFormData({ ...formData, ubicacion: e.target.value })} className="h-11 rounded-xl" />
                </div>
                <div className="space-y-2">
                  <Label>Hora Inicio</Label>
                  <Input type="time" value={formData.hora_inicio} onChange={(e) => setFormData({ ...formData, hora_inicio: e.target.value })} className="h-11 rounded-xl" />
                </div>
                <div className="space-y-2">
                  <Label>Hora Fin</Label>
                  <Input type="time" value={formData.hora_fin} onChange={(e) => setFormData({ ...formData, hora_fin: e.target.value })} className="h-11 rounded-xl" />
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label>Notas</Label>
                  <Textarea value={formData.notas} onChange={(e) => setFormData({ ...formData, notas: e.target.value })} className="rounded-xl resize-none" rows={3} />
                </div>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-6">
                <InfoItem icon={User} label="Nombre" value={cliente.nombre} />
                <InfoItem icon={Phone} label="Teléfono" value={cliente.telefono} />
                <InfoItem icon={Mail} label="Email" value={cliente.email} />
                {cliente.dni_cif_nif && <InfoItem icon={FileText} label="DNI/CIF/NIF" value={cliente.dni_cif_nif} />}
                <InfoItem icon={Calendar} label="Fecha Evento" value={formatDate(cliente.fecha_evento)} />
                <InfoItem icon={Clock} label="Horario" value={cliente.hora_inicio && cliente.hora_fin ? `${cliente.hora_inicio} - ${cliente.hora_fin}` : "No especificado"} />
                <InfoItem icon={MapPin} label="Ubicación" value={cliente.ubicacion} />
                {cliente.notas && <InfoItem icon={FileText} label="Notas" value={cliente.notas} colSpan />}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tasks Card */}
        <Card className="border-0 shadow-soft">
          <CardHeader>
            <CardTitle className="font-heading text-xl flex items-center justify-between">
              <span>Tareas</span>
              <Badge variant="outline">{pendingTasks.length} pendientes</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Input placeholder="Nueva tarea..." value={newTaskTitle} onChange={(e) => setNewTaskTitle(e.target.value)} onKeyPress={(e) => e.key === "Enter" && handleAddTask()} className="h-10 rounded-xl flex-1" />
              <Button size="icon" onClick={handleAddTask} className="rounded-xl h-10 w-10"><Plus className="w-4 h-4" /></Button>
            </div>
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {pendingTasks.map((task) => (
                <TaskItem key={task.id} task={task} onToggle={handleToggleTask} onDelete={handleDeleteTask} />
              ))}
              {completedTasks.length > 0 && (
                <div className="pt-4 border-t border-border/40">
                  <p className="text-sm text-muted-foreground mb-2">Completadas</p>
                  {completedTasks.map((task) => (
                    <TaskItem key={task.id} task={task} onToggle={handleToggleTask} onDelete={handleDeleteTask} completed />
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Facturación */}
      <Card className="border-0 shadow-soft">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading text-xl flex items-center gap-2">
              <Receipt className="w-5 h-5" />
              Facturación
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                className="rounded-full gap-2"
                onClick={handleExportPDF}
                disabled={exportingPDF || !cliente.lineas_factura?.length}
              >
                <Download className="w-4 h-4" />
                {exportingPDF ? "Generando..." : "Descargar PDF"}
              </Button>
              <Dialog open={addServiceDialog} onOpenChange={setAddServiceDialog}>
                <DialogTrigger asChild>
                  <Button className="rounded-full gap-2"><Plus className="w-4 h-4" />Añadir</Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle>Añadir a Factura</DialogTitle>
                    <DialogDescription>Selecciona un pack o servicio extra</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-6 mt-4">
                    {/* Packs */}
                    <div>
                      <Label className="text-sm font-semibold">Packs</Label>
                      <div className="grid grid-cols-2 gap-2 mt-2">
                        {PACKS.map((pack) => (
                          <Button key={pack.nombre} variant="outline" className="h-auto py-3 flex-col items-start rounded-xl" onClick={() => pack.precio > 0 ? handleSelectPack(pack) : setNewService({ concepto: pack.nombre, cantidad: 1, precio_unitario: 0 })}>
                            <span className="font-medium">{pack.nombre}</span>
                            <span className="text-sm text-muted-foreground">{pack.precio > 0 ? `${pack.precio}€` : "Precio a definir"}</span>
                          </Button>
                        ))}
                      </div>
                    </div>
                    {/* Servicios Extra */}
                    <div>
                      <Label className="text-sm font-semibold">Servicios Extra</Label>
                      <div className="grid grid-cols-2 gap-2 mt-2">
                        {SERVICIOS_EXTRA.map((servicio) => (
                          <Button key={servicio.nombre} variant="outline" className="h-auto py-3 flex-col items-start rounded-xl" onClick={() => handleSelectServicioExtra(servicio)}>
                            <span className="font-medium">{servicio.nombre}</span>
                            <span className="text-sm text-muted-foreground">{servicio.precio}€/{servicio.unidad}</span>
                          </Button>
                        ))}
                        <Button variant="outline" className="h-auto py-3 flex-col items-start rounded-xl" onClick={() => setNewService({ concepto: "Otro", cantidad: 1, precio_unitario: 0 })}>
                          <span className="font-medium">Otro</span>
                          <span className="text-sm text-muted-foreground">Personalizado</span>
                        </Button>
                      </div>
                    </div>
                    {/* Formulario personalizado */}
                    {newService.concepto && (
                      <div className="p-4 bg-muted/50 rounded-xl space-y-4">
                        <p className="font-medium">{newService.concepto}</p>
                        {newService.concepto === "Otro" && (
                          <div className="space-y-2">
                            <Label>Nombre del concepto</Label>
                            <Input value={customServiceName} onChange={(e) => setCustomServiceName(e.target.value)} className="h-10 rounded-xl" placeholder="Ej: Servicio especial" />
                          </div>
                        )}
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Cantidad</Label>
                            <Input type="number" min="1" step="1" value={newService.cantidad} onChange={(e) => setNewService({ ...newService, cantidad: parseFloat(e.target.value) || 1 })} className="h-10 rounded-xl" />
                          </div>
                          <div className="space-y-2">
                            <Label>Precio unitario (€)</Label>
                            <Input type="number" min="0" step="0.01" value={newService.precio_unitario} onChange={(e) => setNewService({ ...newService, precio_unitario: parseFloat(e.target.value) || 0 })} className="h-10 rounded-xl" />
                          </div>
                        </div>
                        <div className="flex justify-between items-center pt-2">
                          <span className="text-lg font-semibold">Subtotal: {(newService.cantidad * newService.precio_unitario).toFixed(2)}€</span>
                          <Button onClick={handleAddCustomService} className="rounded-full">Añadir</Button>
                        </div>
                      </div>
                    )}
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Líneas de factura */}
          <div className="space-y-2 mb-6">
            {cliente.lineas_factura?.length > 0 ? (
              <div className="border rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted">
                    <tr>
                      <th className="text-left px-4 py-3 font-medium">Concepto</th>
                      <th className="text-right px-4 py-3 font-medium w-20">Cant.</th>
                      <th className="text-right px-4 py-3 font-medium w-24">Precio</th>
                      <th className="text-right px-4 py-3 font-medium w-24">Subtotal</th>
                      <th className="w-12"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {cliente.lineas_factura.map((linea, idx) => (
                      <tr key={idx} className="border-t border-border/40">
                        <td className="px-4 py-3">{linea.concepto}</td>
                        <td className="px-4 py-3 text-right">{linea.cantidad}</td>
                        <td className="px-4 py-3 text-right">{linea.precio_unitario.toFixed(2)}€</td>
                        <td className="px-4 py-3 text-right font-medium">{linea.subtotal.toFixed(2)}€</td>
                        <td className="px-2 py-3">
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:bg-destructive/10" onClick={() => removeLineaFactura(idx)}>
                            <X className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Receipt className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No hay líneas de factura</p>
              </div>
            )}
          </div>

          {/* Totales */}
          {cliente.lineas_factura?.length > 0 && (
            <div className="flex justify-end">
              <div className="w-72 space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Subtotal</span>
                  <span>{cliente.subtotal?.toFixed(2) || "0.00"}€</span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <div className="flex items-center gap-2">
                    <span>IVA (21%)</span>
                    <Switch checked={cliente.aplicar_iva} onCheckedChange={toggleIVA} />
                  </div>
                  <span>{cliente.iva?.toFixed(2) || "0.00"}€</span>
                </div>
                <div className="flex justify-between text-lg font-bold pt-2 border-t">
                  <span>Total</span>
                  <span>{cliente.total?.toFixed(2) || "0.00"}€</span>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagos */}
      <Card className="border-0 shadow-soft">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading text-xl flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Pagos
            </CardTitle>
            <Dialog open={addPaymentDialog} onOpenChange={setAddPaymentDialog}>
              <DialogTrigger asChild>
                <Button className="rounded-full gap-2"><Plus className="w-4 h-4" />Registrar Pago</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Registrar Pago</DialogTitle>
                  <DialogDescription>Añade un pago o reserva</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label>Fecha</Label>
                    <Input type="date" value={newPayment.fecha} onChange={(e) => setNewPayment({ ...newPayment, fecha: e.target.value })} className="h-11 rounded-xl" />
                  </div>
                  <div className="space-y-2">
                    <Label>Cantidad (€)</Label>
                    <Input type="number" min="0" step="0.01" value={newPayment.cantidad} onChange={(e) => setNewPayment({ ...newPayment, cantidad: parseFloat(e.target.value) || 0 })} className="h-11 rounded-xl" />
                  </div>
                  <div className="space-y-2">
                    <Label>Concepto</Label>
                    <Select value={newPayment.concepto} onValueChange={(v) => setNewPayment({ ...newPayment, concepto: v })}>
                      <SelectTrigger className="h-11 rounded-xl"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="reserva">Reserva / Señal</SelectItem>
                        <SelectItem value="pago_parcial">Pago Parcial</SelectItem>
                        <SelectItem value="pago_final">Pago Final</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={addPago} className="w-full rounded-full">Registrar Pago</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          {cliente.pagos?.length > 0 ? (
            <div className="space-y-2 mb-6">
              {cliente.pagos.map((pago, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-muted/50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <Euro className="w-4 h-4 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium">{pago.cantidad.toFixed(2)}€</p>
                      <p className="text-sm text-muted-foreground">{pago.fecha} - {pago.concepto === "reserva" ? "Reserva" : pago.concepto === "pago_final" ? "Pago Final" : "Pago Parcial"}</p>
                    </div>
                  </div>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:bg-destructive/10" onClick={() => removePago(idx)}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground mb-6">
              <CreditCard className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No hay pagos registrados</p>
            </div>
          )}

          {/* Resumen de pagos */}
          <div className="grid grid-cols-3 gap-4 p-4 bg-muted/30 rounded-xl">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Total Factura</p>
              <p className="text-xl font-bold text-primary">{cliente.total?.toFixed(2) || "0.00"}€</p>
            </div>
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Pagado</p>
              <p className="text-xl font-bold text-green-600">{cliente.total_pagado?.toFixed(2) || "0.00"}€</p>
            </div>
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Pendiente</p>
              <p className="text-xl font-bold text-red-600">{cliente.pendiente_pago?.toFixed(2) || "0.00"}€</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Componentes auxiliares
function InfoItem({ icon: Icon, label, value, colSpan }) {
  return (
    <div className={`flex items-start gap-3 ${colSpan ? "sm:col-span-2" : ""}`}>
      <div className="p-2 bg-primary/10 rounded-lg">
        <Icon className="w-4 h-4 text-primary" strokeWidth={1.5} />
      </div>
      <div>
        <p className="text-sm text-muted-foreground">{label}</p>
        <p className="font-medium text-primary">{value || "-"}</p>
      </div>
    </div>
  );
}

function TaskItem({ task, onToggle, onDelete, completed }) {
  return (
    <div className={`flex items-center gap-3 p-3 rounded-xl group ${completed ? "bg-green-50" : "bg-muted/50"}`}>
      <Checkbox checked={task.completada} onCheckedChange={() => onToggle(task)} />
      <span className={`flex-1 text-sm ${completed ? "text-muted-foreground line-through" : ""}`}>{task.titulo}</span>
      {completed ? (
        <CheckCircle2 className="w-4 h-4 text-green-500" />
      ) : (
        <Button variant="ghost" size="icon" onClick={() => onDelete(task.id)} className="opacity-0 group-hover:opacity-100 h-7 w-7 text-muted-foreground hover:text-destructive">
          <X className="w-3 h-3" />
        </Button>
      )}
    </div>
  );
}
